// @ts-check
// "Keep going" (#199): carry on after the finish, in the same session.
//
// Keep going adds a Free training block to the end of the plan and runs it,
// on the phone, on a Khronos Timer or in a live share alike. While it runs it
// has Free training's log, checklist and Rest button; at the finish the log
// becomes the block's steps, so there is one session and one save, with one
// unbroken heart-rate and bike trace.

import { buildStandaloneSessionPlan } from "./devicePlan";
import { FREE_TRAINING_MODE } from "./freeTraining";
import { logToPlan } from "./freeTrainingLog";
import { genStableId } from "./planEditing";

/**
 * The plan's Free training block that `path` (a compiler tree path, such as
 * ["children", 3, "children", 0]) is in, or null. A whole Free training
 * session is not a block: see isFreeTraining.
 * @param {any} plan
 * @param {any[]} path
 */
export const freeBlockAt = (plan, path) => {
  if (!Array.isArray(path) || path[0] !== "children") return null;
  const block = plan?.children?.[path[1]];
  return block?.freeTraining === true ? block : null;
};

/** @param {any} plan */
const hasOpenFreeBlock = (plan) =>
  Array.isArray(plan?.children) && plan.children.some((c) => c?.freeTraining === true);

/**
 * The plan with a Free training block added at the end, and that block's
 * index among the plan's children.
 * @param {any} plan
 * @returns {{ plan: any, index: number }}
 */
export const appendFreeBlock = (plan) => {
  const preset = /** @type {any} */ (buildStandaloneSessionPlan(FREE_TRAINING_MODE, {}));
  const { countUpMode: _countUp, ...group } = preset;
  const block = {
    ...group,
    id: genStableId(),
    children: group.children.map((leaf) => ({ ...leaf, id: genStableId() })),
  };
  const children = [...(plan?.children || []), block];
  return { plan: { ...plan, children }, index: children.length - 1 };
};

/**
 * The plan with its Free training block settled: what was logged becomes the
 * block's steps, and it is an ordinary block from then on. With nothing
 * logged it keeps one step, as long as the time spent in it.
 * @param {any} plan
 * @param {any[]} log
 * @param {number} servedSec
 */
export const settleFreeBlock = (plan, log = [], servedSec = 0) => {
  if (!hasOpenFreeBlock(plan)) return plan;
  const logged = logToPlan(log);
  const children = plan.children.map((child) => {
    if (child?.freeTraining !== true) return child;
    const { freeTraining: _free, ...block } = child;
    return {
      ...block,
      children: logged?.children?.length
        ? logged.children
        : (block.children || []).map((leaf) => ({
            ...leaf,
            value: Math.max(1, Math.round(servedSec || 0)),
            unit: "s",
          })),
    };
  });
  return { ...plan, children };
};

/**
 * Where a timeline starts the plan's child at `index`: the first block whose
 * path is in it. -1 when there is none.
 * @param {Array<{ path: any[] }>} timeline
 * @param {number} index
 */
export const timelineIndexOfChild = (timeline, index) =>
  (timeline || []).findIndex((b) => b?.path?.[0] === "children" && b.path[1] === index);
